/**
 * 阶段参与人员区块组件
 */

import { Users } from 'lucide-react'

import { useStageParticipants } from '@/hooks'
import { cn } from '@/lib/utils'

import type { WorkflowStage } from '@company/shared'

// ==================== 类型定义 ====================

interface ParticipantsSectionProps {
  issueId: string
  currentStage: WorkflowStage
  className?: string
}

// ==================== 组件实现 ====================

export function ParticipantsSection({
  issueId,
  currentStage,
  className,
}: ParticipantsSectionProps) {
  const { data: participants, isLoading } = useStageParticipants(issueId, currentStage)

  return (
    <div className={cn('bg-card rounded-lg border p-4', className)}>
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4" />
        <h4 className="text-sm font-medium">当前阶段参与人员</h4>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">加载中...</div>
      ) : participants && participants.length > 0 ? (
        <div className="space-y-2">
          {participants.map((p) => (
            <div
              key={p.id}
              className="bg-muted/50 flex items-center justify-between rounded-md px-3 py-2"
            >
              <span className="text-sm">
                {p.employeeName || `员工 ${p.employeeId.slice(0, 8)}`}
              </span>
              <span className="text-muted-foreground text-xs">
                {new Date(p.assignedAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted-foreground text-sm">暂无参与人员</div>
      )}
    </div>
  )
}

/**
 * 紧凑版参与人员列表
 */
interface CompactParticipantsListProps {
  participants: Array<{
    id: string
    employeeId: string
    employeeName: string | null
    assignedAt: string
  }>
  className?: string
}

export function CompactParticipantsList({ participants, className }: CompactParticipantsListProps) {
  if (participants.length === 0) {
    return <span className="text-muted-foreground text-xs">暂无参与人员</span>
  }

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {participants.map((p) => (
        <span
          key={p.id}
          className="bg-muted inline-flex items-center rounded-full px-2 py-0.5 text-xs"
        >
          {p.employeeName || `员工 ${p.employeeId.slice(0, 8)}`}
        </span>
      ))}
    </div>
  )
}
